import { Box, Button, Divider, Flex, Heading, Layout, Text } from '@modusign/design-system';
import { ReactNode, useMemo } from 'react';

import DocumentValidationResult from '../../types/DocumentValidationResult';
import HeaderImage from '../HeaderImage';
import LabeledFileHash from '../LabeledFileHash';

interface FileHashResultPageLayoutProps {
  documentId: string | null;
  headerImageSrc: string;
  result: Omit<DocumentValidationResult, 'isVerified'>;
  resultMessage: ReactNode;
  resultTitle: ReactNode;
  onResetResult: () => void;
}

export default function FileHashResultPageLayout({
  documentId,
  headerImageSrc,
  result,
  resultMessage,
  resultTitle,
  onResetResult,
}: FileHashResultPageLayoutProps) {
  const { originHash, targetHash, verifiedAt, fileName } = result;

  const formattedVerifiedTime = useMemo(
    () => new Date(verifiedAt).toLocaleString('ko-KR'),
    [verifiedAt]
  );

  return (
    <Flex backgroundColor="bodyNeutralBase" height="100%" justifyContent="center" width="100%">
      <Layout flex={1} flexDirection="column" maxWidth="sm" sx={{ boxSizing: 'content-box' }}>
        <Flex justifyContent="center" mt={50}>
          <HeaderImage src={headerImageSrc} />
        </Flex>
        <Flex flexDirection="column" mt={9}>
          <Heading fontSize="md" fontWeight="regular" textAlign="center">
            {fileName}
          </Heading>
          {resultTitle}
        </Flex>
        <Box
          backgroundColor="boxBackgroundNeutralBase"
          borderRadius={6}
          mt={7}
          p={6}
          textAlign="center"
        >
          <Text color="textNeutralLight" fontSize="lg" fontWeight="regular">
            문서 ID : {documentId ?? ''}
          </Text>
        </Box>
        <Flex
          alignItems="center"
          borderColor="boxBorderNeutralBase"
          borderRadius={6}
          borderStyle="solid"
          borderWidth="1px"
          flexDirection="column"
          justifyContent="center"
          mb={6}
          mt={2}
          py={8}
        >
          <Flex flexDirection="column" px={9} width="100%">
            <Text color="textNeutralLight" fontSize="md" fontWeight="medium">
              문서 검증 결과
            </Text>
            {resultMessage}
          </Flex>
          <Divider my={7} />
          <Box mb={4} px={8} width="100%">
            <LabeledFileHash hash={originHash} label="원본 문서" pb={6} />
            <LabeledFileHash hash={targetHash} label="대상 문서" pb={6} />
            <Text color="textNeutralLight" fontSize="xs" fontWeight="regular">
              검증 완료 시각 : {formattedVerifiedTime} (한국 표준시 기준)
            </Text>
          </Box>
        </Flex>
        <Flex alignItems="center" flexDirection="column" mb={10} mt={9}>
          <Button height={48} mb={6} width={180} onClick={onResetResult}>
            검증 결과 초기화하기
          </Button>
          <Text color="textNeutralVeryLight" fontSize="xs" fontWeight="regular">
            데이터 보안을 위해 검증센터를 나가면, 검증 결과를 자동으로 초기화합니다.
          </Text>
        </Flex>
      </Layout>
    </Flex>
  );
}
